import { useCallback, useEffect, useState, useMemo } from "react";
import { Button } from "expano-components";
import { toastr } from "react-redux-toastr";

import { ConfirmModal } from "components";
import { client_v2 } from "utils/api";

import PieceworkerRateScheduleModal from "./PieceworkerRateScheduleModal";

const todayIsoDate = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const createEmptyPieceworkerRateForm = () => ({
  effective_from: todayIsoDate(),
  weekday_rate: "0.8",
  weekend_rate: "0.8",
});

const PieceworkerRatesCard = ({ userId }) => {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteScheduleId, setDeleteScheduleId] = useState(null);

  const loadSchedules = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const body = await client_v2.get(
        `/users/${userId}/pieceworker_rate_schedules`,
      );
      setSchedules(body.data || []);
    } catch (e) {
      toastr.error("Błąd", "Nie udało się pobrać stawek akordowych");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadSchedules();
  }, [loadSchedules]);

  const schedulesByDateDesc = useMemo(
    () =>
      [...schedules].sort((a, b) =>
        String(b.effective_from).localeCompare(String(a.effective_from)),
      ),
    [schedules],
  );

  const modalInitialValues = useMemo(
    () => createEmptyPieceworkerRateForm(),
    [],
  );

  const handleConfirmDelete = async () => {
    if (deleteScheduleId == null) return;
    try {
      await client_v2.delete(
        `/users/${userId}/pieceworker_rate_schedules/${deleteScheduleId}`,
      );
      toastr.success("Sukces", "Usunięto wpis");
      loadSchedules();
    } catch (e) {
      toastr.error("Błąd", "Nie udało się usunąć wpisu");
    } finally {
      setDeleteScheduleId(null);
    }
  };

  return (
    <div className="column-half">
      <div className="dashboard">
        <header className="dashboard__header">
          <h2 className="dashboard__title heading">Stawki akordowe</h2>

          <Button
            type="add"
            text="Nowa stawka"
            onClick={() => setIsModalOpen(true)}
          />
        </header>
        <div className="p-30">
          <p style={{ color: "#666", fontSize: "13px" }}>
            Mnożnik punktów → monety: osobno dla dni powszednich i weekendu
            (sobota, niedziela). Historia po dacie &quot;obowiązuje od&quot; —
            przy przeliczaniu wstecz używany jest wpis obowiązujący w danym
            dniu. Brak wpisów = dotychczasowa stała stawka 0,80.
          </p>
          {schedules.length === 0 && (
            <p className="p-t-10" style={{ fontSize: "13px" }}>
              Brak zdefiniowanych wpisów — używany mnożnik 0,80 (wszystkie dni).
            </p>
          )}
        </div>

        {loading ? (
          <p className="p-t-20">Ładowanie…</p>
        ) : (
          schedules.length > 0 && (
            <div className="p-t-20">
              <table className="table table-sm" style={{ width: "100%" }}>
                <thead className="table__header">
                  <tr>
                    <th>Obowiązuje od</th>
                    <th>Dni powszednie</th>
                    <th>Weekend</th>
                    <th width="100" />
                  </tr>
                </thead>
                <tbody className="table__body">
                  {schedulesByDateDesc.map((row) => (
                    <tr key={row.id}>
                      <td>{row.effective_from}</td>
                      <td>{row.weekday_rate}</td>
                      <td>{row.weekend_rate}</td>
                      <td>
                        <Button
                          type="button"
                          text="Usuń"
                          onClick={() => setDeleteScheduleId(row.id)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>
      {isModalOpen && (
        <PieceworkerRateScheduleModal
          onClose={() => setIsModalOpen(false)}
          userId={userId}
          onSaved={loadSchedules}
          initialValues={modalInitialValues}
        />
      )}
      {deleteScheduleId != null ? (
        <ConfirmModal
          title="Usuwanie wpisu"
          text="Usunąć ten wpis historii stawek?"
          handleClose={() => setDeleteScheduleId(null)}
          handleConfirm={handleConfirmDelete}
        />
      ) : null}
    </div>
  );
};

export default PieceworkerRatesCard;
